<template>
  <div v-if="hasTransition">
    <transition
      leave-active-class="animated mobileNavigationSecondOut fast"
      enter-active-class="animated mobileNavigationSecondIn fast"
    >
      <div
        v-if="showMobileSubLvl"
        ref="subLevel"
        class="mobile-navigation-sub-level-fi"
      >
        <div
          :class="{
            'mobile-navigation-sub-level-fi__header': true,
            'mobile-navigation-sub-level-fi__header--items':
              !isProducts && !isCompany && !isCountrySelect,
            'mobile-navigation-sub-level-fi__header--scrolled': scrolled,
          }"
        >
          <div class="mobile-navigation-sub-level-fi__header--actions">
            <div
              class="mobile-navigation-sub-level-fi__header__back"
              @click="backToFirstLvl"
            >
              <icon-fi
                icon="arrow-left"
                size="2"
              />
            </div>

            <div class="mobile-navigation-sub-level-fi__header__title">
              <template v-if="isCountrySelect">
                {{ $dict.get('global-Labels.Country-Selection.Mobile-Title') }}
              </template>
              <template v-else>
                {{ groupData?.label }}
              </template>
            </div>

            <div
              class="mobile-navigation-sub-level-fi__header__close"
              @click="hideFlyout"
            >
              <icon-fi
                icon="close"
                size="2"
              />
            </div>
          </div>
        </div>
        <mobile-navigation-sub-products-fi v-if="isProducts" />
        <mobile-navigation-sub-companies-fi v-else-if="isCompany" />
        <mobile-navigation-sub-country-select-fi
          v-else-if="isCountrySelect"
          @show-country-selection="onShowCountrySelection"
        />
        <mobile-navigation-sub-items-fi
          v-else
          @scroll.passive="handleScroll"
        />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
import { computed, onMounted, ref, watch } from 'vue';

import { useBodyScroll } from '@/composables/bodyScroll';
import { useBreakpoints } from '@/composables/breakpoint';
import { useNavigationStore } from '@/store/pinia/navigation';

import MobileNavigationSubCompaniesFi from './mobile-navigation-sub-companies-fi/MobileNavigationSubCompaniesFi.vue';
import MobileNavigationSubCountrySelectFi from './mobile-navigation-sub-country-select-fi/MobileNavigationSubCountrySelectFi.vue';
import MobileNavigationSubItemsFi from './mobile-navigation-sub-items-fi/MobileNavigationSubItemsFi.vue';
import MobileNavigationSubProductsFi from './mobile-navigation-sub-products-fi/MobileNavigationSubProductsFi.vue';

defineOptions({ name: 'MobileNavigationSubLevelFi' });

const navigationStore = useNavigationStore();
const { isSM, isLG, windowDimensions } = useBreakpoints();
const { bodyEnableScroll } = useBodyScroll();

const scrolled = ref(false);
const hasTransition = ref(true);
let wasLG: boolean | null = null;

const groupData = computed(() => navigationStore.groupData);
const isProducts = computed(() => navigationStore.isProducts);
const isCompany = computed(() => navigationStore.isCompany);
const showMobileSubLvl = computed(() => navigationStore.showMobileSubLvl);
const isCountrySelect = computed(() => navigationStore.isCountrySelect);

const windowResized = () => {
  if (isSM.value) {
    if (wasLG === isLG.value && !hasTransition.value) {
      return;
    }
    wasLG = isLG.value;
    hasTransition.value = false;
    navigationStore.setShowMobileFirstLvl(false);
    navigationStore.setShowMobileSubLvl(false);
    navigationStore.setShowFlyout(isCountrySelect.value);
  } else {
    hasTransition.value = true;
  }
};

watch(
  () => windowDimensions.value,
  () => {
    windowResized();
  },
  { deep: true },
);

onMounted(() => {
  hasTransition.value = !isSM.value;
});

const backToFirstLvl = () => {
  scrolled.value = false;
  navigationStore.setSubElements([]);
  navigationStore.setGroupData(undefined);
  navigationStore.setNavIndex(-1);
  navigationStore.setShowMobileSubLvl(false);
  navigationStore.setShowMobileFirstLvl(true);
};

const hideFlyout = () => {
  scrolled.value = false;
  bodyEnableScroll();
  navigationStore.setShowBrandArea(true);
  navigationStore.setShowMobileSubLvl(false);
  navigationStore.setIsCountrySelect(false);
};

const handleScroll = (e: Event) => {
  if ((e.target as HTMLElement).scrollTop > 30) {
    scrolled.value = true;
  } else {
    scrolled.value = false;
  }
};

const onShowCountrySelection = () => {
  hideFlyout();
};
</script>

<style scoped src="./mobile-navigation-sub-level-fi" />
